import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, useToast } from '../../components/primitives';
import { SendEmailModal } from '../../components/SendEmailModal';
import { buildDocHtml } from '../../lib/docHtml';
import { printToPdf } from '../../lib/printToPdf';
import {
  DocLayout,
  DocPage,
  DocBlock,
  DocRow,
  DocSignatures,
  DocContactQR,
  DocQRInline,
  DocInlineDate,
  DocInlineChips,
  DocInlineAresName,
  DocInlineAresLookup,
  DocActionButton,
  DraftBanner,
  useDocChain,
  useDocTransferIn,
  DocChainBar,
  DocTransferButton,
  DocNotes,
  createDocNote,
  type DocNote,
  SavedDocsButton,
  NewDocButton,
  useDocRows,
  DocCustomRows,
  DocAddRowButton,
  type DocRowsSnapshot,
} from '../../components/document';
import { MapPickerButton } from '../../components/map';
import {
  CompanyPickerButton,
  SaveCompanyButton,
  ContactPickerButton,
  type AresPickResult,
  type CompanyPickResult,
  type ContactPerson,
} from '../../components/ares';
import {
  StavbaPickerButton,
  SaveStavbaButton,
  type StavbaPickResult,
} from '../../components/stavba';
import { useSaveDocument } from '../../api/hooks';
import { useDocEditor } from '../../lib/useDocEditor';
import { useDocDraft } from '../../lib/useDocDraft';
import { docSaveErrorMessage } from '../../lib/docSaveError';
import { useActiveStavba } from '../stavba-context/StavbaContext';

/**
 * Předávací protokol — dílo.
 * Stejný "živý dokument" jako u staveniště, ale obsah míří na předání
 * a převzetí hotového díla: zhotovitel předává, objednatel přebírá.
 */

type UcastnikPreset = 'tdi' | 'tds' | 'bozp' | 'autorsky' | 'other';

interface Ucastnik {
  id: string;
  preset: UcastnikPreset;
  strana: string;
  firma: string;
  jmeno: string;
  funkce: string;
  tel: string;
  email: string;
}

const PRESET_LABELS: Record<UcastnikPreset, { strana: string; funkce: string }> = {
  tdi: { strana: 'Technický dozor investora (TDI)', funkce: 'TDI / TDS' },
  tds: { strana: 'Technický dozor stavebníka (TDS)', funkce: 'TDS' },
  bozp: { strana: 'Koordinátor BOZP', funkce: 'Koordinátor BOZP' },
  autorsky: { strana: 'Autorský dozor', funkce: 'Autorský dozor' },
  other: { strana: 'Další účastník', funkce: '' },
};

type DiloData = {
  nazev: string;
  adresa: string;
  cPovoleni: string;
  cSmlouvy: string;
  /** Podle čeho se dílo čísluje: smlouva o dílo, nebo objednávka. */
  cisloTyp: 'sod' | 'objednavka';
  objednatel: {
    name: string;
    address: string;
    ico: string;
    zastoupeny: string;
    phone: string;
    email: string;
  };
  zhotovitel: {
    name: string;
    address: string;
    ico: string;
    ckait: string;
    stavbyvedouci: string;
    phone: string;
    email: string;
  };
  datumDokonceni: string;
  datumPredani: string;
  smluvniTermin: string;
  zarukaDo: string;
  vadyText: string;
  terminVad: string;
  poznamky: string;
  smluvniCena: string;
  signatures: {
    zhotovitel: string;
    objednatel: string;
    ucastnici: Record<string, string>;
  };
  ucastnici: Ucastnik[];
  /** Volitelné textové rámečky sekce „Ostatní". */
  ostatni: DocNote[];
};

const initialData: DiloData = {
  nazev: '',
  adresa: '',
  cPovoleni: '',
  cSmlouvy: '',
  cisloTyp: 'sod',
  objednatel: {
    name: '',
    address: '',
    ico: '',
    zastoupeny: '',
    phone: '',
    email: '',
  },
  zhotovitel: {
    name: '',
    address: '',
    ico: '',
    ckait: '',
    stavbyvedouci: '',
    phone: '',
    email: '',
  },
  datumDokonceni: '',
  datumPredani: '',
  smluvniTermin: '',
  zarukaDo: '',
  vadyText: '',
  terminVad: '',
  poznamky: '',
  smluvniCena: '',
  signatures: { zhotovitel: '', objednatel: '', ucastnici: {} },
  ucastnici: [],
  ostatni: [],
};

const dokladyChipsInitial = [
  { id: 'pd', label: 'Projektová dokumentace skutečného provedení', checked: true },
  { id: 'zkousky', label: 'Doklady o zkouškách', checked: true },
  { id: 'atesty', label: 'Atesty materiálů', checked: false },
  { id: 'zaruky', label: 'Záruční listy', checked: false },
  { id: 'navody', label: 'Návody k obsluze', checked: false },
  { id: 'revize', label: 'Revizní zprávy', checked: false },
];
const dokladyFixedIds = dokladyChipsInitial.map((c) => c.id);

const vysledekChipsInitial = [
  { id: 'bez-vyhrad', label: 'Dílo převzato bez výhrad', checked: false },
  { id: 's-vyhradami', label: 'Dílo převzato s výhradami (viz níže)', checked: false },
  { id: 'neprevzato', label: 'Dílo nebylo převzato', checked: false },
];
const vysledekFixedIds = vysledekChipsInitial.map((c) => c.id);

function newUcastnikId(): string {
  return `u-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

function createUcastnik(preset: UcastnikPreset): Ucastnik {
  const p = PRESET_LABELS[preset];
  return {
    id: newUcastnikId(),
    preset,
    strana: p.strana,
    funkce: p.funkce,
    firma: '',
    jmeno: '',
    tel: '',
    email: '',
  };
}

type DiloDraft = DiloData & {
  dokladyChips: typeof dokladyChipsInitial;
  vysledekChips: typeof vysledekChipsInitial;
  /** Skryté + vlastní řádky hlavičkových sekcí. */
  radky?: DocRowsSnapshot;
  /** Starší koncepty/archiv — jen klíče skrytých řádků (před zavedením `radky`). */
  hiddenRows?: string[];
  /** Odkaz na doklad, ze kterého protokol vznikl (řetězení dokladů). */
  zdroj?: unknown;
};

export function ProtocolDiloPage() {
  const [data, setData] = useState<DiloData>(initialData);
  const [dokladyChips, setDokladyChips] = useState(dokladyChipsInitial);
  const [vysledekChips, setVysledekChips] = useState(vysledekChipsInitial);
  const [emailOpen, setEmailOpen] = useState(false);
  const previewRef = useRef<HTMLDivElement | null>(null);
  const saveDoc = useSaveDocument();
  const toast = useToast();
  const { activeStavba } = useActiveStavba();

  // Skryté řádky — uživatel může jednotlivé řádky odebrat ✕,
  // skupinové „obnovit" je pak v hlavičce sekce. Plus vlastní řádky („+ Řádek").
  const chain = useDocChain();
  const rows = useDocRows();
  const { isHidden, hide, restoreSection, hiddenCount } = rows;

  // Číslo díla: smlouva o dílo / objednávka / bez čísla (řádek skrytý).
  const cisloSkryto = isHidden('s01.smlouvy');
  const cisloLabel = data.cisloTyp === 'objednavka' ? 'Č. objednávky' : 'Č. smlouvy o dílo';
  const cisloMeta = data.cisloTyp === 'objednavka' ? 'obj. č.' : 'SoD č.';
  const cisloValue = cisloSkryto ? '' : data.cSmlouvy.trim();

  const setSignature = (who: 'zhotovitel' | 'objednatel', dataUrl: string) =>
    setData((d) => ({ ...d, signatures: { ...d.signatures, [who]: dataUrl } }));
  const setUcastnikSignature = (id: string, dataUrl: string) =>
    setData((d) => ({
      ...d,
      signatures: {
        ...d.signatures,
        ucastnici: { ...d.signatures.ucastnici, [id]: dataUrl },
      },
    }));

  /** Předvyplní formulář ze snímku dat (archiv i převod z jiného dokladu). */
  function applySnapshot(raw: Record<string, unknown>) {
    const {
      dokladyChips: savedDoklady,
      vysledekChips: savedVysledek,
      hiddenRows,
      radky,
      zdroj: _zdroj,
      ...rest
    } = raw;
    chain.apply(raw);
    setData((prev) => ({ ...prev, ...(rest as Partial<DiloData>) }));
    if (savedDoklady) setDokladyChips(savedDoklady as typeof dokladyChipsInitial);
    if (savedVysledek) setVysledekChips(savedVysledek as typeof vysledekChipsInitial);
    // Zpětná kompatibilita: starší záznamy mají jen `hiddenRows: string[]`.
    rows.apply(
      radky ?? (Array.isArray(hiddenRows) ? { hidden: hiddenRows, custom: {} } : undefined),
    );
  }
  const { editId } = useDocEditor(applySnapshot);
  useDocTransferIn('predavaci-dilo', applySnapshot, editId);

  /** Kompletní snímek dokumentu (archiv, koncept, převod na fakturu). */
  const docData = () => ({
    ...data, dokladyChips, vysledekChips, radky: rows.snapshot(), zdroj: chain.zdroj,
  });
  const docTitle = `Předávací protokol díla ${cisloValue || data.nazev}`.trim();

  // Autosave rozpracovaného konceptu do prohlížeče (jen u nového formuláře).
  const draft = useDocDraft<DiloDraft>('predavaci-dilo', editId == null);
  useEffect(() => {
    draft.persist(docData());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, dokladyChips, vysledekChips, rows, chain.zdroj]);

  function restoreDraft() {
    if (!draft.pending) return;
    chain.apply(draft.pending.data as unknown as Record<string, unknown>);
    const { dokladyChips: dc, vysledekChips: vc, hiddenRows, radky, zdroj: _zdroj, ...rest } =
      draft.pending.data;
    setData((prev) => ({ ...prev, ...(rest as Partial<DiloData>) }));
    if (dc) setDokladyChips(dc);
    if (vc) setVysledekChips(vc);
    rows.apply(
      radky ?? (Array.isArray(hiddenRows) ? { hidden: hiddenRows, custom: {} } : undefined),
    );
    draft.accept();
  }

  async function handleSave(): Promise<number | null> {
    try {
      const res = await saveDoc.mutateAsync({
        id: editId ?? chain.savedId ?? undefined,
        doc_type: 'predavaci-dilo',
        title: docTitle,
        stavba: activeStavba?.nazev ?? '',
        data_json: docData(),
      });
      draft.clear();
      toast.success('Dokument uložen do archivu.');
      chain.markSaved(res.id);
      return res.id;
    } catch (e) {
      toast.error(docSaveErrorMessage(e));
      return null;
    }
  }

  const filled = useMemo(() => {
    const allValues = [
      data.nazev, data.adresa,
      ...(isHidden('s01.povoleni') ? [] : [data.cPovoleni]),
      ...(cisloSkryto ? [] : [data.cSmlouvy]),
      data.objednatel.name, data.objednatel.address, data.objednatel.ico, data.objednatel.zastoupeny, data.objednatel.phone, data.objednatel.email,
      data.zhotovitel.name, data.zhotovitel.address, data.zhotovitel.ico, data.zhotovitel.ckait, data.zhotovitel.stavbyvedouci, data.zhotovitel.phone, data.zhotovitel.email,
      data.datumDokonceni, data.datumPredani, data.smluvniTermin, data.zarukaDo, data.smluvniCena,
    ];
    const total = allValues.length;
    const filledCount = allValues.filter((v) => v && v.trim().length > 0).length;
    return Math.round((filledCount / total) * 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isHidden]);

  const toggleDoklad = (id: string) =>
    setDokladyChips((prev) => prev.map((c) => (c.id === id ? { ...c, checked: !c.checked } : c)));
  const addDoklad = (label: string) =>
    setDokladyChips((prev) => [
      ...prev,
      { id: `custom-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, label, checked: true },
    ]);
  const removeDoklad = (id: string) => setDokladyChips((prev) => prev.filter((c) => c.id !== id));

  const toggleVysledek = (id: string) =>
    setVysledekChips((prev) =>
      // výsledek přejímky je v praxi jedna volba → chovej se jako přepínač
      prev.map((c) => (c.id === id ? { ...c, checked: !c.checked } : { ...c, checked: false })),
    );

  const set = <K extends keyof DiloData>(key: K, value: DiloData[K]) =>
    setData((d) => ({ ...d, [key]: value }));
  const setObjednatel = (patch: Partial<DiloData['objednatel']>) =>
    setData((d) => ({ ...d, objednatel: { ...d.objednatel, ...patch } }));
  const setZhotovitel = (patch: Partial<DiloData['zhotovitel']>) =>
    setData((d) => ({ ...d, zhotovitel: { ...d.zhotovitel, ...patch } }));

  const addUcastnik = (preset: UcastnikPreset) =>
    setData((d) => ({ ...d, ucastnici: [...d.ucastnici, createUcastnik(preset)] }));
  const updateUcastnik = (id: string, patch: Partial<Ucastnik>) =>
    setData((d) => ({
      ...d,
      ucastnici: d.ucastnici.map((u) => (u.id === id ? { ...u, ...patch } : u)),
    }));
  const removeUcastnik = (id: string) =>
    setData((d) => ({ ...d, ucastnici: d.ucastnici.filter((u) => u.id !== id) }));

  // Volitelná sekce „Ostatní" — libovolný počet textových rámečků.
  const addOstatni = () => setData((d) => ({ ...d, ostatni: [...d.ostatni, createDocNote()] }));
  const updateOstatni = (id: string, patch: Partial<DocNote>) =>
    setData((d) => ({
      ...d,
      ostatni: d.ostatni.map((n) => (n.id === id ? { ...n, ...patch } : n)),
    }));
  const removeOstatni = (id: string) =>
    setData((d) => ({ ...d, ostatni: d.ostatni.filter((n) => n.id !== id) }));

  const addUcastnikActions = (
    <>
      <DocActionButton onClick={() => addUcastnik('tdi')}>+ TDI</DocActionButton>
      <DocActionButton onClick={() => addUcastnik('tds')}>+ TDS</DocActionButton>
      <DocActionButton onClick={() => addUcastnik('bozp')}>+ BOZP</DocActionButton>
      <DocActionButton onClick={() => addUcastnik('autorsky')}>+ Autorský</DocActionButton>
      <DocActionButton onClick={() => addUcastnik('other')}>+ Vlastní</DocActionButton>
    </>
  );

  return (
    <>
      <DocLayout
        breadcrumb={['Předání', 'Dílo']}
        title="Protokol o předání díla"
        statusText="Rozpracovaný koncept"
        status="draft"
        formProgress={{ percent: filled, label: 'Vyplněno' }}
        actions={
          <>
            <NewDocButton draftKey="predavaci-dilo" path="/app/predavaci-dilo" />
            <SavedDocsButton
              docType="predavaci-dilo"
              title="Uložené předávací protokoly — dílo"
              currentId={editId}
            />
            <Button
              variant="secondary"
              leading="⎙"
              onClick={() =>
                printToPdf(
                  `Predavaci_protokol_dilo_${cisloValue || data.nazev || 'dilo'}.pdf`,
                )
              }
            >
              PDF
            </Button>
            <Button variant="secondary" leading="📧" onClick={() => setEmailOpen(true)}>
              Odeslat e-mailem
            </Button>
            <DocTransferButton
              docType="predavaci-dilo"
              getData={docData}
              save={handleSave}
              title={docTitle}
              zdroj={chain.zdroj}
            />
            <Button variant="primary" onClick={handleSave} disabled={saveDoc.isPending}>
              {saveDoc.isPending ? 'Ukládám…' : saveDoc.isSuccess ? 'Uloženo ✓' : 'Uložit'}
            </Button>
          </>
        }
      >
        <DocChainBar zdroj={chain.zdroj} />
        {draft.pending && (
          <DraftBanner
            savedAt={draft.pending.savedAt}
            onRestore={restoreDraft}
            onDiscard={draft.clear}
          />
        )}
        <div ref={previewRef}>
          <DocPage
            company={{
              initials: 'SN',
              name: data.zhotovitel.name,
              address: `${data.zhotovitel.address || 'Adresa'} · IČO ${data.zhotovitel.ico}`,
            }}
            tag="Předávací protokol — dílo"
            title="Protokol o předání a převzetí díla"
            meta={[cisloValue && `${cisloMeta} ${cisloValue}`, data.datumPredani]
              .filter(Boolean)
              .join(' · ')}
            footer={{ left: data.zhotovitel.name, center: 'str. 1 / 1', right: cisloValue }}
          >
            <DocBlock
              idx="01"
              title="Identifikace díla"
              hiddenCount={hiddenCount('s01')}
              onRestoreHidden={() => restoreSection('s01')}
              actions={
                <>
                  <DocAddRowButton rows={rows} section="s01" />
                  <StavbaPickerButton
                    title="Načíst stavbu z adresáře"
                    onPick={(s: StavbaPickResult) =>
                      setData((d) => ({
                        ...d,
                        nazev: s.nazev,
                        adresa: s.adresa,
                        cPovoleni: s.stavebni_povoleni,
                      }))
                    }
                  />
                  <SaveStavbaButton
                    title="Uložit stavbu do adresáře"
                    build={() => ({
                      nazev: data.nazev,
                      adresa: data.adresa,
                      stavebni_povoleni: data.cPovoleni,
                    })}
                  />
                </>
              }
            >
              {!isHidden('s01.nazev') && (
                <DocRow
                  k="Název díla / stavby"
                  value={data.nazev}
                  onChange={(v) => set('nazev', v)}
                  placeholder="např. Novostavba RD…"
                  onRemove={() => hide('s01.nazev')}
                />
              )}
              {!isHidden('s01.adresa') && (
                <DocRow
                  k="Místo (adresa)"
                  value={data.adresa}
                  onChange={(v) => set('adresa', v)}
                  placeholder="ulice č.p., PSČ město"
                  actions={
                    <MapPickerButton
                      value={data.adresa}
                      onApply={(v) => set('adresa', v)}
                      title="Vybrat místo díla"
                      label="Mapa"
                    />
                  }
                  onRemove={() => hide('s01.adresa')}
                />
              )}
              {!isHidden('s01.povoleni') && (
                <DocRow
                  k="Č. stav. povolení"
                  mono
                  value={data.cPovoleni}
                  onChange={(v) => set('cPovoleni', v)}
                  placeholder="např. MÚ-2026/0341 (nepovinné)"
                  actions={
                    <DocActionButton
                      title="Stavba žádné povolení nemá — řádek se v dokumentu nezobrazí"
                      onClick={() => hide('s01.povoleni')}
                    >
                      Nevyplňovat
                    </DocActionButton>
                  }
                  onRemove={() => hide('s01.povoleni')}
                  removeTitle="Nevyplňovat — řádek se nezobrazí"
                />
              )}
              {!cisloSkryto && (
                <DocRow
                  k={cisloLabel}
                  mono
                  value={data.cSmlouvy}
                  onChange={(v) => set('cSmlouvy', v)}
                  placeholder={
                    data.cisloTyp === 'objednavka' ? 'např. OBJ-2026/0412' : 'např. SoD-2026/0412'
                  }
                  actions={
                    <>
                      <DocActionButton
                        variant={data.cisloTyp === 'sod' ? 'solid' : 'ghost'}
                        aria-pressed={data.cisloTyp === 'sod'}
                        title="Číslovat podle smlouvy o dílo"
                        onClick={() => set('cisloTyp', 'sod')}
                      >
                        Smlouva o dílo
                      </DocActionButton>
                      <DocActionButton
                        variant={data.cisloTyp === 'objednavka' ? 'solid' : 'ghost'}
                        aria-pressed={data.cisloTyp === 'objednavka'}
                        title="Číslovat podle objednávky"
                        onClick={() => set('cisloTyp', 'objednavka')}
                      >
                        Objednávka
                      </DocActionButton>
                      <DocActionButton
                        title="Dílo nemá smlouvu ani objednávku — řádek se v dokumentu nezobrazí"
                        onClick={() => hide('s01.smlouvy')}
                      >
                        Bez čísla
                      </DocActionButton>
                    </>
                  }
                  onRemove={() => hide('s01.smlouvy')}
                  removeTitle="Bez čísla — řádek se nezobrazí"
                />
              )}
              <DocCustomRows rows={rows} section="s01" />
            </DocBlock>

            <DocBlock
              idx="02"
              title="Zhotovitel (předávající)"
              hiddenCount={hiddenCount('s02')}
              onRestoreHidden={() => restoreSection('s02')}
              actions={
                <>
                  <DocAddRowButton rows={rows} section="s02" />
                  <CompanyPickerButton
                    preferRole="zhotovitel"
                    title="Načíst zhotovitele z adresáře"
                    onPick={(c: CompanyPickResult) =>
                      setZhotovitel({
                        name: c.nazev,
                        ico: c.ico,
                        address: c.adresa,
                        ckait: c.ckait,
                        stavbyvedouci: c.stavbyvedouci || c.kontakt_jmeno,
                        phone: c.kontakt_tel,
                        email: c.kontakt_email,
                      })
                    }
                  />
                  <SaveCompanyButton
                    title="Uložit zhotovitele do adresáře"
                    build={() => ({
                      role: 'zhotovitel',
                      nazev: data.zhotovitel.name,
                      ico: data.zhotovitel.ico,
                      adresa: data.zhotovitel.address,
                      kontakt_jmeno: data.zhotovitel.stavbyvedouci,
                      kontakt_tel: data.zhotovitel.phone,
                      kontakt_email: data.zhotovitel.email,
                      stavbyvedouci: data.zhotovitel.stavbyvedouci,
                      ckait: data.zhotovitel.ckait,
                    })}
                  />
                </>
              }
            >
              {!isHidden('s02.firma') && (
                <DocRow
                  k="Firma"
                  v={
                    <DocInlineAresName
                      value={data.zhotovitel.name}
                      onChange={(v) => setZhotovitel({ name: v })}
                      placeholder="např. Stavby Novák s.r.o."
                      onPick={(d: AresPickResult) =>
                        setZhotovitel({ name: d.nazev, ico: d.ico, address: d.adresa })
                      }
                    />
                  }
                  onRemove={() => hide('s02.firma')}
                />
              )}
              {!isHidden('s02.sidlo') && (
                <DocRow
                  k="Sídlo"
                  value={data.zhotovitel.address}
                  onChange={(v) => setZhotovitel({ address: v })}
                  placeholder="ulice č.p., PSČ město"
                  actions={
                    <MapPickerButton
                      value={data.zhotovitel.address}
                      onApply={(v) => setZhotovitel({ address: v })}
                      title="Sídlo zhotovitele"
                      label="Mapa"
                    />
                  }
                  onRemove={() => hide('s02.sidlo')}
                />
              )}
              {!isHidden('s02.ico') && (
                <DocRow
                  k="IČO"
                  mono
                  value={data.zhotovitel.ico}
                  onChange={(v) => setZhotovitel({ ico: v })}
                  placeholder="12345678"
                  actions={
                    <DocInlineAresLookup
                      ico={data.zhotovitel.ico}
                      onResult={(d) =>
                        setZhotovitel({ name: d.nazev, ico: d.ico, address: d.adresa })
                      }
                    />
                  }
                  onRemove={() => hide('s02.ico')}
                />
              )}
              {!isHidden('s02.stavbyvedouci') && (
                <DocRow
                  k="Stavbyvedoucí"
                  value={data.zhotovitel.stavbyvedouci}
                  onChange={(v) => setZhotovitel({ stavbyvedouci: v })}
                  placeholder="jméno – vyplňte nebo vyberte z adresáře"
                  actions={
                    <ContactPickerButton
                      attachToCompany={data.zhotovitel.name}
                      companyDraft={{ role: 'zhotovitel', ico: data.zhotovitel.ico, adresa: data.zhotovitel.address }}
                      draftNew={{
                        jmeno: data.zhotovitel.stavbyvedouci,
                        funkce: 'stavbyvedoucí',
                        tel: data.zhotovitel.phone,
                        email: data.zhotovitel.email,
                      }}
                      title="Stavbyvedoucí — z adresáře"
                      onPick={(p: ContactPerson) =>
                        setZhotovitel({
                          stavbyvedouci: p.jmeno,
                          ...(p.tel ? { phone: p.tel } : {}),
                          ...(p.email ? { email: p.email } : {}),
                        })
                      }
                    />
                  }
                  onRemove={() => hide('s02.stavbyvedouci')}
                />
              )}
              {!isHidden('s02.ckait') && (
                <DocRow
                  k="Č. autorizace ČKAIT"
                  mono
                  value={data.zhotovitel.ckait}
                  onChange={(v) => setZhotovitel({ ckait: v })}
                  placeholder="např. 1002847"
                  onRemove={() => hide('s02.ckait')}
                />
              )}
              {!isHidden('s02.tel') && (
                <DocRow
                  k="Telefon"
                  mono
                  value={data.zhotovitel.phone}
                  onChange={(v) => setZhotovitel({ phone: v })}
                  inputType="tel"
                  placeholder="+420 …"
                  onRemove={() => hide('s02.tel')}
                />
              )}
              {!isHidden('s02.email') && (
                <DocRow
                  k="E-mail"
                  mono
                  value={data.zhotovitel.email}
                  onChange={(v) => setZhotovitel({ email: v })}
                  inputType="email"
                  placeholder="email@…"
                  actions={
                    <DocContactQR
                      name={data.zhotovitel.stavbyvedouci || data.zhotovitel.name}
                      role="Stavbyvedoucí"
                      org={data.zhotovitel.name}
                      phone={data.zhotovitel.phone}
                      email={data.zhotovitel.email}
                      title="QR vizitka — stavbyvedoucí"
                    />
                  }
                  onRemove={() => hide('s02.email')}
                />
              )}
              {!isHidden('s02.qr') && (
                <DocRow
                  k="QR vizitka"
                  v={
                    <DocQRInline
                      name={data.zhotovitel.stavbyvedouci || data.zhotovitel.name}
                      role="Stavbyvedoucí"
                      org={data.zhotovitel.name}
                      phone={data.zhotovitel.phone}
                      email={data.zhotovitel.email}
                      caption="Zhotovitel"
                    />
                  }
                  onRemove={() => hide('s02.qr')}
                />
              )}
              <DocCustomRows rows={rows} section="s02" />
            </DocBlock>

            <DocBlock
              idx="03"
              title="Objednatel (přebírající)"
              hiddenCount={hiddenCount('s03')}
              onRestoreHidden={() => restoreSection('s03')}
              actions={
                <>
                  <DocAddRowButton rows={rows} section="s03" />
                  <CompanyPickerButton
                    preferRole="objednatel"
                    title="Načíst objednatele z adresáře"
                    onPick={(c: CompanyPickResult) =>
                      setObjednatel({
                        name: c.nazev,
                        ico: c.ico,
                        address: c.adresa,
                        zastoupeny: c.kontakt_jmeno,
                        phone: c.kontakt_tel,
                        email: c.kontakt_email,
                      })
                    }
                  />
                  <SaveCompanyButton
                    title="Uložit objednatele do adresáře"
                    build={() => ({
                      role: 'objednatel',
                      nazev: data.objednatel.name,
                      ico: data.objednatel.ico,
                      adresa: data.objednatel.address,
                      kontakt_jmeno: data.objednatel.zastoupeny,
                      kontakt_tel: data.objednatel.phone,
                      kontakt_email: data.objednatel.email,
                    })}
                  />
                </>
              }
            >
              {!isHidden('s03.firma') && (
                <DocRow
                  k="Název / Jméno"
                  v={
                    <DocInlineAresName
                      value={data.objednatel.name}
                      onChange={(v) => setObjednatel({ name: v })}
                      placeholder="jméno objednatele / firma"
                      onPick={(d: AresPickResult) =>
                        setObjednatel({ name: d.nazev, ico: d.ico, address: d.adresa })
                      }
                    />
                  }
                  onRemove={() => hide('s03.firma')}
                />
              )}
              {!isHidden('s03.adresa') && (
                <DocRow
                  k="Sídlo / Adresa"
                  value={data.objednatel.address}
                  onChange={(v) => setObjednatel({ address: v })}
                  placeholder="ulice č.p., PSČ město"
                  actions={
                    <MapPickerButton
                      value={data.objednatel.address}
                      onApply={(v) => setObjednatel({ address: v })}
                      title="Adresa objednatele"
                      label="Mapa"
                    />
                  }
                  onRemove={() => hide('s03.adresa')}
                />
              )}
              {!isHidden('s03.ico') && (
                <DocRow
                  k="IČO"
                  mono
                  value={data.objednatel.ico}
                  onChange={(v) => setObjednatel({ ico: v })}
                  placeholder="12345678"
                  actions={
                    <DocInlineAresLookup
                      ico={data.objednatel.ico}
                      onResult={(d) =>
                        setObjednatel({ name: d.nazev, ico: d.ico, address: d.adresa })
                      }
                    />
                  }
                  onRemove={() => hide('s03.ico')}
                />
              )}
              {!isHidden('s03.zastoupeny') && (
                <DocRow
                  k="Zastoupený"
                  value={data.objednatel.zastoupeny}
                  onChange={(v) => setObjednatel({ zastoupeny: v })}
                  placeholder="jméno – vyplňte nebo vyberte z adresáře"
                  actions={
                    <ContactPickerButton
                      attachToCompany={data.objednatel.name}
                      companyDraft={{ role: 'objednatel', ico: data.objednatel.ico, adresa: data.objednatel.address }}
                      draftNew={{
                        jmeno: data.objednatel.zastoupeny,
                        tel: data.objednatel.phone,
                        email: data.objednatel.email,
                      }}
                      title="Zástupce objednatele — z adresáře"
                      onPick={(p: ContactPerson) =>
                        setObjednatel({
                          zastoupeny: p.jmeno,
                          ...(p.tel ? { phone: p.tel } : {}),
                          ...(p.email ? { email: p.email } : {}),
                        })
                      }
                    />
                  }
                  onRemove={() => hide('s03.zastoupeny')}
                />
              )}
              {!isHidden('s03.tel') && (
                <DocRow
                  k="Telefon"
                  mono
                  value={data.objednatel.phone}
                  onChange={(v) => setObjednatel({ phone: v })}
                  inputType="tel"
                  placeholder="+420 …"
                  onRemove={() => hide('s03.tel')}
                />
              )}
              {!isHidden('s03.email') && (
                <DocRow
                  k="E-mail"
                  mono
                  value={data.objednatel.email}
                  onChange={(v) => setObjednatel({ email: v })}
                  inputType="email"
                  placeholder="email@…"
                  actions={
                    <DocContactQR
                      name={data.objednatel.zastoupeny || data.objednatel.name}
                      role="Objednatel"
                      org={data.objednatel.name}
                      phone={data.objednatel.phone}
                      email={data.objednatel.email}
                      title="QR vizitka — objednatel"
                    />
                  }
                  onRemove={() => hide('s03.email')}
                />
              )}
              {!isHidden('s03.qr') && (
                <DocRow
                  k="QR vizitka"
                  v={
                    <DocQRInline
                      name={data.objednatel.zastoupeny || data.objednatel.name}
                      role="Objednatel"
                      org={data.objednatel.name}
                      phone={data.objednatel.phone}
                      email={data.objednatel.email}
                      caption="Objednatel"
                    />
                  }
                  onRemove={() => hide('s03.qr')}
                />
              )}
              <DocCustomRows rows={rows} section="s03" />
            </DocBlock>

            <DocBlock idx="04" title="Další účastníci" actions={addUcastnikActions}>
              {data.ucastnici.length === 0 ? (
                <DocRow
                  k="—"
                  v={
                    <span style={{ color: 'var(--sd-doc-dim)', fontStyle: 'italic' }}>
                      Žádní další účastníci. Přidej je tlačítky vpravo.
                    </span>
                  }
                />
              ) : (
                data.ucastnici.map((u, i) => (
                  <UcastnikRows
                    key={u.id}
                    u={u}
                    index={i}
                    onUpdate={(patch) => updateUcastnik(u.id, patch)}
                    onRemove={() => removeUcastnik(u.id)}
                  />
                ))
              )}
            </DocBlock>

            <DocBlock
              idx="05"
              title="Předání díla"
              hiddenCount={hiddenCount('s05')}
              onRestoreHidden={() => restoreSection('s05')}
              actions={<DocAddRowButton rows={rows} section="s05" />}
            >
              {!isHidden('s05.dokonceni') && (
                <DocRow
                  k="Dílo dokončeno dne"
                  mono
                  value={data.datumDokonceni}
                  onChange={(v) => set('datumDokonceni', v)}
                  placeholder="dd. mm. rrrr"
                  actions={
                    <DocInlineDate
                      value={data.datumDokonceni}
                      onChange={(v) => set('datumDokonceni', v)}
                    />
                  }
                  onRemove={() => hide('s05.dokonceni')}
                />
              )}
              {!isHidden('s05.predani') && (
                <DocRow
                  k="Dílo předáno dne"
                  mono
                  value={data.datumPredani}
                  onChange={(v) => set('datumPredani', v)}
                  placeholder="dd. mm. rrrr"
                  actions={
                    <DocInlineDate
                      value={data.datumPredani}
                      onChange={(v) => set('datumPredani', v)}
                    />
                  }
                  onRemove={() => hide('s05.predani')}
                />
              )}
              {!isHidden('s05.smluvniTermin') && (
                <DocRow
                  k="Smluvní termín dokončení"
                  mono
                  value={data.smluvniTermin}
                  onChange={(v) => set('smluvniTermin', v)}
                  placeholder="dd. mm. rrrr"
                  actions={
                    <DocInlineDate
                      value={data.smluvniTermin}
                      onChange={(v) => set('smluvniTermin', v)}
                    />
                  }
                  onRemove={() => hide('s05.smluvniTermin')}
                />
              )}
              {!isHidden('s05.zaruka') && (
                <DocRow
                  k="Záruční doba do"
                  mono
                  value={data.zarukaDo}
                  onChange={(v) => set('zarukaDo', v)}
                  placeholder="dd. mm. rrrr"
                  actions={
                    <DocInlineDate
                      value={data.zarukaDo}
                      onChange={(v) => set('zarukaDo', v)}
                    />
                  }
                  onRemove={() => hide('s05.zaruka')}
                />
              )}
              {!isHidden('s05.doklady') && (
                <DocRow
                  k="Předané doklady"
                  v={
                    <DocInlineChips
                      items={dokladyChips}
                      onToggle={toggleDoklad}
                      onAdd={addDoklad}
                      onRemove={removeDoklad}
                      fixedIds={dokladyFixedIds}
                    />
                  }
                  onRemove={() => hide('s05.doklady')}
                />
              )}
              <DocCustomRows rows={rows} section="s05" />
            </DocBlock>

            <DocBlock
              idx="06"
              title="Vady a nedodělky"
              hiddenCount={hiddenCount('s06')}
              onRestoreHidden={() => restoreSection('s06')}
            >
              {!isHidden('s06.vysledek') && (
                <DocRow
                  k="Výsledek přejímky"
                  v={
                    <DocInlineChips
                      items={vysledekChips}
                      onToggle={toggleVysledek}
                      fixedIds={vysledekFixedIds}
                    />
                  }
                  onRemove={() => hide('s06.vysledek')}
                />
              )}
              {!isHidden('s06.vady') && (
                <DocRow
                  k="Vady a nedodělky"
                  multiline
                  value={data.vadyText}
                  onChange={(v) => set('vadyText', v)}
                  placeholder="Soupis vad a nedodělků, případně odkaz na přílohu"
                  onRemove={() => hide('s06.vady')}
                />
              )}
              {!isHidden('s06.terminVad') && (
                <DocRow
                  k="Termín odstranění vad"
                  mono
                  value={data.terminVad}
                  onChange={(v) => set('terminVad', v)}
                  placeholder="dd. mm. rrrr"
                  actions={
                    <DocInlineDate
                      value={data.terminVad}
                      onChange={(v) => set('terminVad', v)}
                    />
                  }
                  onRemove={() => hide('s06.terminVad')}
                />
              )}
              {!isHidden('s06.poznamky') && (
                <DocRow
                  k="Poznámky"
                  multiline
                  value={data.poznamky}
                  onChange={(v) => set('poznamky', v)}
                  placeholder="Další ujednání, výhrady, předané klíče…"
                  onRemove={() => hide('s06.poznamky')}
                />
              )}
              {!isHidden('s06.cena') && (
                <DocRow
                  k="Smluvní cena díla celkem"
                  value={data.smluvniCena}
                  onChange={(v) => set('smluvniCena', v)}
                  placeholder="např. 1 250 000 Kč bez DPH"
                  onRemove={() => hide('s06.cena')}
                />
              )}
            </DocBlock>

            {data.ostatni.length > 0 ? (
              <DocBlock
                idx="07"
                title="Ostatní"
                actions={
                  <DocActionButton onClick={addOstatni}>+ Přidat rámeček</DocActionButton>
                }
              >
                <DocNotes items={data.ostatni} onChange={updateOstatni} onRemove={removeOstatni} />
              </DocBlock>
            ) : (
              <div className="docNoPrint" style={{ marginTop: 14 }}>
                <DocActionButton
                  onClick={addOstatni}
                  title="Volitelná sekce — poznámky, připomínky, vyjádření TDS…"
                >
                  + Přidat sekci „Ostatní"
                </DocActionButton>
              </div>
            )}

            <DocSignatures
              items={[
                {
                  label: 'Zhotovitel (předávající)',
                  name: data.zhotovitel.stavbyvedouci,
                  role: 'stavbyvedoucí',
                  image: data.signatures.zhotovitel,
                  onSign: (v) => setSignature('zhotovitel', v),
                },
                {
                  label: 'Objednatel (přebírající)',
                  name: data.objednatel.zastoupeny || data.objednatel.name,
                  role: 'přebírající',
                  image: data.signatures.objednatel,
                  onSign: (v) => setSignature('objednatel', v),
                },
                ...data.ucastnici.map((u) => ({
                  label: u.strana || 'Další účastník',
                  name: u.jmeno || '—',
                  role: u.funkce || '',
                  image: data.signatures.ucastnici[u.id] || '',
                  onSign: (v: string) => setUcastnikSignature(u.id, v),
                })),
              ]}
            />
          </DocPage>
        </div>
      </DocLayout>

      <SendEmailModal
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        buildHtml={() => buildDocHtml(previewRef.current)}
        docType="predavaci-dilo"
        docTitle={`Předávací protokol díla — ${data.nazev || 'dílo'}`}
        defaultTo={data.objednatel.email}
        defaultCc={data.zhotovitel.email}
        defaultSubject={`Předávací protokol díla — ${data.nazev}${cisloValue ? ' (č. ' + cisloValue + ')' : ''}`}
        meta={`Stavba: ${data.nazev || '—'}${cisloValue ? ' · ' + cisloMeta + ' ' + cisloValue : ''}${activeStavba?.nazev ? ' · ' + activeStavba.nazev : ''}`}
      />
    </>
  );
}

/** Účastník vykreslený přímo jako sekce řádků v dokumentu. */
function UcastnikRows({
  u,
  index,
  onUpdate,
  onRemove,
}: {
  u: Ucastnik;
  index: number;
  onUpdate: (patch: Partial<Ucastnik>) => void;
  onRemove: () => void;
}) {
  const preferRole =
    u.preset === 'tdi' || u.preset === 'tds'
      ? 'tds'
      : u.preset === 'bozp'
        ? 'bozp_koordinator'
        : u.preset === 'autorsky'
          ? 'projektant'
          : undefined;

  const saveRole =
    u.preset === 'bozp'
      ? 'bozp_koordinator'
      : u.preset === 'tdi' || u.preset === 'tds'
        ? 'tds'
        : u.preset === 'autorsky'
          ? 'projektant'
          : 'subdodavatel';

  return (
    <>
      <DocRow
        k={`Účastník ${index + 1}`}
        value={u.strana}
        onChange={(v) => onUpdate({ strana: v })}
        placeholder="Strana / role"
        actions={
          <>
            <CompanyPickerButton
              preferRole={preferRole}
              title={`Načíst ${u.strana} z adresáře`}
              onPick={(c) =>
                onUpdate({
                  firma: c.nazev,
                  jmeno: c.kontakt_jmeno || c.stavbyvedouci || u.jmeno,
                  tel: c.kontakt_tel,
                  email: c.kontakt_email,
                })
              }
            />
            <SaveCompanyButton
              title={`Uložit ${u.strana} do adresáře`}
              build={() => ({
                role: saveRole,
                nazev: u.firma || u.strana,
                kontakt_jmeno: u.jmeno,
                kontakt_tel: u.tel,
                kontakt_email: u.email,
                bozp_koordinator: u.preset === 'bozp' ? u.jmeno : '',
                bozp_tel: u.preset === 'bozp' ? u.tel : '',
                poznamka: u.funkce,
              })}
            />
            <DocActionButton onClick={onRemove} title="Odebrat účastníka">
              ✕
            </DocActionButton>
          </>
        }
      />
      <DocRow
        k="Firma"
        value={u.firma}
        onChange={(v) => onUpdate({ firma: v })}
        placeholder="název firmy"
      />
      <DocRow
        k="Jméno a příjmení"
        value={u.jmeno}
        onChange={(v) => onUpdate({ jmeno: v })}
        placeholder="jméno – vyplňte nebo vyberte z adresáře"
      />
      <DocRow
        k="Funkce"
        value={u.funkce}
        onChange={(v) => onUpdate({ funkce: v })}
        placeholder="funkce"
      />
      <DocRow
        k="Telefon"
        mono
        value={u.tel}
        onChange={(v) => onUpdate({ tel: v })}
        inputType="tel"
        placeholder="+420 …"
      />
      <DocRow
        k="E-mail"
        mono
        value={u.email}
        onChange={(v) => onUpdate({ email: v })}
        inputType="email"
        placeholder="email@…"
        actions={
          <DocContactQR
            name={u.jmeno}
            role={u.funkce || u.strana}
            org={u.firma}
            phone={u.tel}
            email={u.email}
            compact
            label="QR"
            title={`QR vizitka — ${u.strana}`}
          />
        }
      />
    </>
  );
}
